import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, Tabs, Breadcrumb, Layout, Skeleton } from 'antd';
import { CalendarOutlined, HomeOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppHeaderHomePageTemplate from '../Organism/AppHeaderHomePageTemplate';
import { format } from 'date-fns';
import './ApteanCommunique.css';
import { useTranslation } from 'react-i18next';

const { TabPane } = Tabs;

interface CommuniqueDetails {
    headerText: string;
    description: string;
    imageName: string;
    createdDate: Date;
    communiqueId: string;
    pdfFileName: string;
}

// Derive image path from tab + child tab
const getImagePath = (tab: string, childTab: string, imageName: string): string => {
    if (tab === 'announcement') return `images/Announcement/${imageName}`;
    if (tab === 'newsletter')   return `images/NewsLetter/${imageName}`;
    const sub = childTab.charAt(0).toUpperCase() + childTab.slice(1); // "it" → "It" ≡ folder names
    return `images/Alerts/${sub}/${imageName}`;
};

const FALLBACK_IMG = 'images/placeholder.png';

// Reusable card — images are lazy-loaded natively
const CommuniqueCard: React.FC<{
    card: CommuniqueDetails;
    tab: string;
    childTab: string;
    onClick: () => void;
}> = ({ card, tab, childTab, onClick }) => (
    <Card
        bordered
        style={{ width: '23%', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        cover={
            <img
                alt={card.headerText}
                src={getImagePath(tab, childTab, card.imageName)}
                loading="lazy"
                decoding="async"
                onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMG; }}
                style={{ objectFit: 'cover', height: '150px', cursor: 'pointer' }}
                onClick={onClick}
            />
        }
    >
        <div style={{ fontWeight: 600, fontSize: '14px', color: '#000' }}>{card.headerText}</div>
        <p style={{ fontWeight: 400, fontSize: '12px', color: '#888' }}>{card.description}</p>
        <p style={{ fontWeight: 400, fontSize: '12px', color: '#000' }}>
            <CalendarOutlined /> {format(new Date(card.createdDate), 'dd MMM, yyyy')}
            <span style={{ float: 'right' }}>
                {card.pdfFileName && (
                    <a href={`pdfs/${card.pdfFileName}`} target="_blank" rel="noopener noreferrer" style={{ color: '#000' }}>
                        <FilePdfOutlined style={{ color: '#d32f2f' }} />
                    </a>
                )}
            </span>
        </p>
    </Card>
);

// Skeleton placeholder grid while loading
const SkeletonGrid: React.FC = () => (
    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} style={{ width: '23%', borderRadius: '8px' }}>
                <Skeleton.Image active style={{ width: '100%', height: '120px', marginBottom: 12 }} />
                <Skeleton active paragraph={{ rows: 2 }} />
            </Card>
        ))}
    </div>
);

const ApteanCommunique: React.FC = () => {
    const [activeTab, setActiveTab]           = useState('announcement');
    const [activeChildTab, setActiveChildTab] = useState('it');
    const [loading, setLoading]               = useState(false);
    const [communiqueData, setCommuniqueData] = useState<CommuniqueDetails[]>([]);
    const { instance, accounts } = useMsal();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const baseURL = process.env.REACT_APP_API_URL;

    // Per-tab cache: key = "tab_childTab", value = { data, timestamp }
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes — re-fetch after this to pick up new data
    const cacheRef = useRef<Map<string, { data: CommuniqueDetails[]; ts: number }>>(new Map());

    const fetchToken = useCallback(async () => {
        const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
        return res.idToken;
    }, [instance, accounts]);

    const fetchData = useCallback(async (tab: string, childTab: string) => {
        const cacheKey = tab === 'alerts' ? `${tab}_${childTab}` : tab;

        // Serve from cache if still fresh (within TTL)
        const cached = cacheRef.current.get(cacheKey);
        if (cached && Date.now() - cached.ts < CACHE_TTL) {
            setCommuniqueData(cached.data);
            return;
        }

        try {
            setLoading(true);
            const token = await fetchToken();
            const url = tab === 'alerts'
                ? `${baseURL}/api/COECommunique/GetAllActiveCommuniqueByCategory?category=${tab}&sub_category=${childTab}`
                : `${baseURL}/api/COECommunique/GetAllActiveCommuniqueByCategory?category=${tab}`;

            const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
            const data: CommuniqueDetails[] = response.status === 200 ? response.data : [];
            cacheRef.current.set(cacheKey, { data, ts: Date.now() });
            setCommuniqueData(data);
        } catch (error) {
            console.error('Unexpected error in fetchData:', error);
        } finally {
            setLoading(false);
        }
    }, [fetchToken, baseURL]);

    useEffect(() => {
        fetchData(activeTab, activeChildTab);
    }, [activeTab, activeChildTab, fetchData]);

    const handleTabChange = (key: string) => {
        setActiveTab(key);
    };

    const handleChildTabKey = (key: string) => {
        setActiveChildTab(key);
        setActiveTab('alerts');
    };

    const handleCardClick = (id: string, tab: string, child: string) => {
        navigate('/ViewCommunique', { state: { communique_id: id, activeTab: tab, activeChildTab: child } });
    };

    const renderCards = (tab: string, childTab: string) => (
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'left' }}>
            {communiqueData.map((card, i) => (
                <CommuniqueCard
                    key={card.communiqueId ?? i}
                    card={card}
                    tab={tab}
                    childTab={childTab}
                    onClick={() => handleCardClick(card.communiqueId, tab, childTab)}
                />
            ))}
        </div>
    );

    return (
        <Layout style={{ background: '#F9FAFB' }}>
            <AppHeaderHomePageTemplate />
            <div style={{ maxWidth: '100%', margin: '0 auto', padding: '0px', minHeight: '100vh', width: '62rem' }}>

                <Breadcrumb style={{ backgroundColor: 'white', padding: '20px 20px 10px' }}>
                    <Breadcrumb.Item>
                        <Link to="/home"><HomeOutlined style={{ marginRight: '5px' }} />{t('home')}</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>{t('communique')}</Breadcrumb.Item>
                </Breadcrumb>

                <div style={{ backgroundColor: 'white', padding: '0px 20px', fontWeight: 600, fontSize: '22px', color: '#000' }}>
                    {t('communique')}
                </div>
                <div style={{ backgroundColor: 'white', padding: '0px 20px', fontWeight: 400, fontSize: '12px', color: '#000' }}>
                    {t('communique_heading')}
                </div>

                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    type="card"
                    style={{ marginBottom: '10px', backgroundColor: 'white', padding: '15px 10px' }}
                >
                    <TabPane tab={t('communique')} key="announcement">
                        {loading ? <SkeletonGrid /> : renderCards('announcement', '')}
                    </TabPane>

                    <TabPane tab={t('newsletters')} key="newsletter">
                        {loading ? <SkeletonGrid /> : renderCards('newsletter', '')}
                    </TabPane>

                    <TabPane tab={t('alerts')} key="alerts">
                        <Tabs type="card" style={{ marginTop: '0px' }} activeKey={activeChildTab} onChange={handleChildTabKey}>
                            <TabPane tab={t('it')} key="it">
                                {loading ? <SkeletonGrid /> : renderCards('alerts', 'it')}
                            </TabPane>
                            <TabPane tab={t('hr')} key="hr">
                                {loading ? <SkeletonGrid /> : renderCards('alerts', 'hr')}
                            </TabPane>
                            <TabPane tab={t('finance')} key="finance">
                                {loading ? <SkeletonGrid /> : renderCards('alerts', 'finance')}
                            </TabPane>
                            <TabPane tab={t('facilities')} key="facilities">
                                {loading ? <SkeletonGrid /> : renderCards('alerts', 'facilities')}
                            </TabPane>
                        </Tabs>
                    </TabPane>
                </Tabs>

            </div>
        </Layout>
    );
};

export default ApteanCommunique;
